import axios from "api/axios";
import { formatTrackerRegistrationDateTime } from "pages/tracker-registration/trackerRegistrationPayload";
import { uploadTrackerFileResource } from "pages/tracker-registration/trackerRegistrationApi";
import {
  extractTrackerBulkloadJobError,
  pollTrackerBulkloadJob,
} from "utils/trackerBulkload";

export const PETITION_DOCUMENT_TRACKER = {
  programId: "FqKvO9ANhh4",
  trackedEntityTypeId: "p5iCr1OtsLj",
  attachmentProgramStageId: "KC0lMmD73jA",
  attributes: {
    year: "Ik7fouv4KOA",
    assessmentType: "QGGHKvNuTRU",
    petitionText: "fGfO0hrRRgq",
    documentName: "gV5C5XfkCR5",
    documentReference: "usccm7IEAoD",
  },
  attachmentDataElement: "U9Af3wVFn6L",
};

const { programId, trackedEntityTypeId, attributes: ATTR, attachmentDataElement } =
  PETITION_DOCUMENT_TRACKER;

let cachedAttachmentProgramStageId = null;

function generateDhis2Uid() {
  const letters = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const characters = `${letters}0123456789`;
  const firstCharacter = letters[Math.floor(Math.random() * letters.length)];
  const remainingCharacters = Array.from(
    { length: 10 },
    () => characters[Math.floor(Math.random() * characters.length)],
  ).join("");

  return `${firstCharacter}${remainingCharacters}`;
}

function extractTrackerResponseRecords(data) {
  return data?.instances || data?.trackedEntities || data?.trackedEntityInstances || [];
}

function wait(delayMs) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, delayMs);
  });
}

function extractTrackerJobErrorMessage(report) {
  const validationMessage = report?.validationReport?.errorReports?.[0]?.message;
  if (validationMessage) {
    return validationMessage;
  }

  const typeReportMap = report?.bundleReport?.typeReportMap || {};
  for (const typeReport of Object.values(typeReportMap)) {
    for (const objectReport of typeReport?.objectReports || []) {
      const nestedMessage = objectReport?.errorReports?.[0]?.message;
      if (nestedMessage) {
        return nestedMessage;
      }
    }
  }

  return extractTrackerBulkloadJobError(report);
}

function extractImportedTrackerEventId(report, fallbackEventId = null) {
  const objectReports =
    report?.bundleReport?.typeReportMap?.EVENT?.objectReports ||
    report?.bundleReport?.typeReports?.EVENT?.objectReports ||
    [];

  const firstReport = Array.isArray(objectReports) ? objectReports[0] : null;

  return (
    firstReport?.uid ||
    fallbackEventId ||
    null
  );
}

async function submitAndPollTrackerImport(
  payload,
  { importStrategy = "CREATE_AND_UPDATE", onStatus } = {},
) {
  const response = await axios.post(
    `/tracker?async=true&importStrategy=${importStrategy}&skipRuleEngine=true&skipAudit=true`,
    payload,
  );
  const jobId = response?.data?.response?.id;

  if (!jobId) {
    throw new Error(
      response?.data?.message ||
        "The tracker import was submitted, but no job reference was returned.",
    );
  }

  const report = await pollTrackerBulkloadJob(jobId, { onStatus });

  if (String(report?.status || "").toUpperCase() === "ERROR") {
    throw new Error(extractTrackerJobErrorMessage(report));
  }

  return { jobId, report, response: response?.data ?? null };
}

async function waitForFileResourceStored(fileResourceId, { maxAttempts = 10, intervalMs = 1000 } = {}) {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const response = await axios.get(`/fileResources/${fileResourceId}`);
    const storageStatus = String(response?.data?.storageStatus || "").toUpperCase();

    if (storageStatus === "STORED") {
      return response?.data;
    }

    if (storageStatus === "ERROR") {
      throw new Error("The uploaded PDF could not be stored on the server.");
    }

    await wait(intervalMs);
  }

  return null;
}

export async function resolveDistrictName(orgUnitId, districtName = "") {
  const trimmedName = String(districtName || "").trim();
  if (trimmedName) {
    return trimmedName;
  }

  if (!orgUnitId) {
    return "District";
  }

  try {
    const response = await axios.get(`/organisationUnits/${orgUnitId}`, {
      params: { fields: "id,name,displayName" },
    });
    return response?.data?.displayName || response?.data?.name || "District";
  } catch {
    return "District";
  }
}

function sanitizeReferencePart(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9-_]/g, "");
}

export function buildPetitionDocumentReference(districtName, year) {
  const districtPart = sanitizeReferencePart(districtName) || "District";
  return `Petition-${districtPart}-${year}`;
}

export function buildPetitionDocumentName(year) {
  return `Petition ${year}`;
}

function buildPetitionTrackerAttributes({ year, petitionPlainText, districtName }) {
  return [
    { attribute: ATTR.year, value: String(year) },
    { attribute: ATTR.assessmentType, value: "DPAT" },
    { attribute: ATTR.petitionText, value: petitionPlainText },
    { attribute: ATTR.documentName, value: buildPetitionDocumentName(year) },
    { attribute: ATTR.documentReference, value: buildPetitionDocumentReference(districtName, year) },
  ];
}

function buildPetitionTrackerPayload({
  orgUnitId,
  attributes,
  trackedEntityId = null,
  enrollmentId = null,
  isUpdate = false,
}) {
  const submissionDateTime = formatTrackerRegistrationDateTime(new Date());
  const resolvedTrackedEntityId = trackedEntityId || generateDhis2Uid();
  const resolvedEnrollmentId = enrollmentId || generateDhis2Uid();
  const enrollmentAttributes = attributes.map((attribute) => ({ ...attribute }));

  if (isUpdate && trackedEntityId) {
    return {
      trackedEntities: [
        {
          trackedEntity: resolvedTrackedEntityId,
          orgUnit: orgUnitId,
          trackedEntityType: trackedEntityTypeId,
          attributes,
        },
      ],
      meta: {
        trackedEntityId: resolvedTrackedEntityId,
        enrollmentId: resolvedEnrollmentId,
      },
    };
  }

  return {
    trackedEntities: [
      {
        trackedEntity: resolvedTrackedEntityId,
        orgUnit: orgUnitId,
        trackedEntityType: trackedEntityTypeId,
        attributes,
        enrollments: [
          {
            enrollment: resolvedEnrollmentId,
            program: programId,
            orgUnit: orgUnitId,
            enrolledAt: submissionDateTime,
            occurredAt: submissionDateTime,
            status: "ACTIVE",
            attributes: enrollmentAttributes,
          },
        ],
      },
    ],
    meta: {
      trackedEntityId: resolvedTrackedEntityId,
      enrollmentId: resolvedEnrollmentId,
    },
  };
}

function recordMatchesPetition(record, { documentReference }) {
  const attrs = Array.isArray(record?.attributes) ? record.attributes : [];
  const refValue = attrs.find((item) => item.attribute === ATTR.documentReference)?.value;
  return Boolean(refValue && refValue === documentReference);
}

export async function getDocumentAttachmentProgramStageId() {
  if (cachedAttachmentProgramStageId) {
    return cachedAttachmentProgramStageId;
  }

  if (PETITION_DOCUMENT_TRACKER.attachmentProgramStageId) {
    cachedAttachmentProgramStageId = PETITION_DOCUMENT_TRACKER.attachmentProgramStageId;
    return cachedAttachmentProgramStageId;
  }

  const response = await axios.get(`/programs/${programId}`, {
    params: {
      fields: "programStages[id,name,programStageDataElements[dataElement[id]]]",
    },
  });

  const stages = response?.data?.programStages || [];
  const matchingStage = stages.find((stage) =>
    (stage?.programStageDataElements || []).some(
      (entry) => entry?.dataElement?.id === attachmentDataElement,
    ),
  );

  cachedAttachmentProgramStageId = matchingStage?.id || stages[0]?.id || null;

  if (!cachedAttachmentProgramStageId) {
    throw new Error("Assessment document tracker stage could not be resolved.");
  }

  return cachedAttachmentProgramStageId;
}

export async function findPetitionTrackerRecord({ orgUnitId, year, documentReference }) {
  if (!orgUnitId || year == null) {
    return null;
  }

  const response = await axios.get("/tracker/trackedEntities", {
    params: {
      orgUnit: orgUnitId,
      program: programId,
      startDate: `${year}-01-01`,
      endDate: `${year}-12-31`,
      pageSize: 100,
      fields:
        "trackedEntity,orgUnit,attributes[attribute,value],enrollments[enrollment,program,status,events[event,programStage,occurredAt,status,dataValues[dataElement,value]]]",
    },
  });

  const records = extractTrackerResponseRecords(response?.data || {});
  return records.find((record) => recordMatchesPetition(record, { documentReference })) || null;
}

async function resolveEnrollmentId(trackedEntityId) {
  const response = await axios.get(`/tracker/trackedEntities/${trackedEntityId}`, {
    params: { fields: "trackedEntity,enrollments[enrollment,program]" },
  });

  const enrollments = response?.data?.enrollments || [];
  return enrollments.find((entry) => entry?.program === programId)?.enrollment || null;
}

function extractAttachmentDetailsFromRecord(record) {
  const enrollments = Array.isArray(record?.enrollments) ? record.enrollments : [];
  const events = enrollments.flatMap((enrollment) =>
    Array.isArray(enrollment?.events) ? enrollment.events : [],
  );

  for (const eventItem of events) {
    const dataValues = Array.isArray(eventItem?.dataValues) ? eventItem.dataValues : [];
    const attachment = dataValues.find(
      (dataValue) => dataValue?.dataElement === attachmentDataElement && dataValue?.value,
    );
    if (attachment?.value) {
      return {
        fileResourceId: attachment.value,
        eventId: eventItem?.event || null,
      };
    }
  }

  return null;
}

function getAxiosBasicAuthHeader() {
  const auth = axios?.defaults?.auth;
  if (!auth?.username || !auth?.password || typeof window === "undefined") {
    return "";
  }

  return `Basic ${window.btoa(`${auth.username}:${auth.password}`)}`;
}

function getFileResourceRequestConfig(extraConfig = {}) {
  const authorization = getAxiosBasicAuthHeader();

  return {
    ...extraConfig,
    headers: {
      ...(extraConfig.headers || {}),
      ...(authorization ? { Authorization: authorization } : {}),
    },
  };
}

export function buildPetitionEventFileDownloadUrl(eventId) {
  if (!eventId) {
    return "";
  }

  const params = new URLSearchParams({
    eventUid: eventId,
    dataElementUid: attachmentDataElement,
  });

  return `/events/files?${params.toString()}`;
}

function normalizePreviewContentType(contentType, fileName = "") {
  const type = String(contentType || "")
    .toLowerCase()
    .split(";")[0]
    .trim();
  const ext = String(fileName || "")
    .trim()
    .match(/\.([a-z0-9]+)$/i)?.[1]
    ?.toLowerCase();

  if (type === "application/pdf" || ext === "pdf") {
    return "application/pdf";
  }

  return type || "application/octet-stream";
}

async function fetchPetitionDocumentPreviewBlob({
  fileResourceId,
  eventId,
  eventFileDownloadUrl,
  fileName = "",
}) {
  const urls = [];

  if (eventFileDownloadUrl) {
    urls.push(eventFileDownloadUrl);
  }

  if (eventId) {
    urls.push(buildPetitionEventFileDownloadUrl(eventId));
  }

  if (fileResourceId) {
    urls.push(`/fileResources/${fileResourceId}/data`);
    urls.push(`/fileResources/${fileResourceId}/data?attachment=true`);
  }

  let response = null;
  let lastError = null;

  for (const url of urls) {
    try {
      response = await axios.get(url, getFileResourceRequestConfig({ responseType: "blob" }));
      break;
    } catch (error) {
      lastError = error;
    }
  }

  if (!response) {
    throw lastError || new Error("Document preview failed.");
  }

  const contentType = normalizePreviewContentType(
    response.headers["content-type"],
    fileName,
  );
  const disposition = response?.headers?.["content-disposition"] || "";
  const dispositionFileName =
    disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1] ||
    disposition.match(/filename="?([^"]+)"?/i)?.[1] ||
    "";
  const resolvedFileName = decodeURIComponent(dispositionFileName || fileName || "").trim();
  const blob = new Blob([response.data], { type: contentType });

  return {
    blobUrl: URL.createObjectURL(blob),
    contentType,
    fileName: resolvedFileName || fileName || "petition-document.pdf",
  };
}

async function findAttachmentEvent(trackedEntityId, programStageId) {
  const response = await axios.get("/tracker/events", {
    params: {
      program: programId,
      trackedEntity: trackedEntityId,
      pageSize: 50,
      fields: "event,programStage,occurredAt,status,dataValues[dataElement,value]",
    },
  });

  const events = response?.data?.events || [];
  return (
    events.find(
      (eventItem) =>
        eventItem?.programStage === programStageId ||
        (eventItem?.dataValues || []).some(
          (dataValue) => dataValue?.dataElement === attachmentDataElement,
        ),
    ) || null
  );
}

export async function fetchPetitionDocumentInfo({
  orgUnitId,
  year,
  districtName,
  trackedEntityId,
  fileResourceId: fileResourceIdHint,
  eventId: eventIdHint,
}) {
  let fileResourceId = fileResourceIdHint || null;
  let eventId = eventIdHint || null;
  let resolvedTrackedEntityId = trackedEntityId || null;

  if (!fileResourceId || !eventId) {
    const resolvedDistrictName = await resolveDistrictName(orgUnitId, districtName);
    const documentReference = buildPetitionDocumentReference(resolvedDistrictName, year);
    const record =
      (resolvedTrackedEntityId
        ? { trackedEntity: resolvedTrackedEntityId, enrollments: [] }
        : null) ||
      (await findPetitionTrackerRecord({ orgUnitId, year, documentReference }));

    if (record) {
      resolvedTrackedEntityId = record.trackedEntity || resolvedTrackedEntityId;
      const attachmentDetails = extractAttachmentDetailsFromRecord(record);
      fileResourceId = fileResourceId || attachmentDetails?.fileResourceId || null;
      eventId = eventId || attachmentDetails?.eventId || null;
    }
  }

  if ((!fileResourceId || !eventId) && resolvedTrackedEntityId) {
    const programStageId = await getDocumentAttachmentProgramStageId();
    const eventItem = await findAttachmentEvent(resolvedTrackedEntityId, programStageId);
    const attachment = (eventItem?.dataValues || []).find(
      (dataValue) => dataValue?.dataElement === attachmentDataElement,
    );
    fileResourceId = fileResourceId || attachment?.value || null;
    eventId = eventId || eventItem?.event || null;
  }

  if (!fileResourceId) {
    return {
      trackedEntityId: resolvedTrackedEntityId,
      fileResourceId: null,
      fileName: null,
      eventId: null,
      eventFileDownloadUrl: "",
    };
  }

  let fileName = "Petition document.pdf";

  try {
    const response = await axios.get(
      `/fileResources/${fileResourceId}`,
      getFileResourceRequestConfig(),
    );
    fileName = response?.data?.displayName || response?.data?.name || fileName;
  } catch {
    // Fall back to generic filename when metadata is unavailable.
  }

  return {
    trackedEntityId: resolvedTrackedEntityId,
    fileResourceId,
    fileName,
    eventId,
    eventFileDownloadUrl: buildPetitionEventFileDownloadUrl(eventId),
  };
}

export async function downloadPetitionDocument({
  fileResourceId,
  fileName,
  eventId,
  eventFileDownloadUrl,
} = {}) {
  const { blobUrl, fileName: resolvedFileName } = await fetchPetitionDocumentPreviewBlob({
    fileResourceId,
    eventId,
    eventFileDownloadUrl,
    fileName,
  });

  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = resolvedFileName || fileName || "petition-document.pdf";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
}

export async function fetchPetitionDocumentPreview({
  fileResourceId,
  eventId,
  eventFileDownloadUrl,
  fileName,
} = {}) {
  return fetchPetitionDocumentPreviewBlob({
    fileResourceId,
    eventId,
    eventFileDownloadUrl,
    fileName,
  });
}

export async function syncPetitionToDocumentTracker({
  orgUnitId,
  districtName,
  year,
  petitionPlainText,
  pdfFile,
  existingTrackedEntityId,
  existingEnrollmentId,
  onStatus,
}) {
  if (!orgUnitId || year == null) {
    throw new Error("District and assessment year are required for document tracker sync.");
  }

  if (!(pdfFile instanceof File)) {
    throw new Error("A PDF petition document is required.");
  }

  const resolvedDistrictName = await resolveDistrictName(orgUnitId, districtName);
  const documentReference = buildPetitionDocumentReference(resolvedDistrictName, year);
  const attributes = buildPetitionTrackerAttributes({
    year,
    petitionPlainText,
    districtName: resolvedDistrictName,
  });

  onStatus?.("Registering assessment document tracker…");

  let trackedEntityId = existingTrackedEntityId || null;
  let enrollmentId = existingEnrollmentId || null;
  const existingRecord =
    trackedEntityId || enrollmentId
      ? null
      : await findPetitionTrackerRecord({ orgUnitId, year, documentReference });

  if (existingRecord?.trackedEntity) {
    trackedEntityId = existingRecord.trackedEntity;
    enrollmentId =
      existingRecord.enrollments?.find((entry) => entry?.program === programId)?.enrollment || null;
  }

  const isUpdate = Boolean(trackedEntityId);
  const payload = buildPetitionTrackerPayload({
    orgUnitId,
    attributes,
    trackedEntityId,
    enrollmentId,
    isUpdate,
  });

  trackedEntityId = payload.trackedEntities[0].trackedEntity;
  enrollmentId = enrollmentId || payload.meta?.enrollmentId || null;

  await submitAndPollTrackerImport(payload, {
    importStrategy: isUpdate ? "UPDATE" : "CREATE_AND_UPDATE",
    onStatus: () => onStatus?.("Saving tracker registration…"),
  });

  if (!enrollmentId) {
    enrollmentId = await resolveEnrollmentId(trackedEntityId);
  }

  if (!enrollmentId) {
    throw new Error("The petition tracker was saved, but no program enrollment was found.");
  }

  onStatus?.("Uploading petition document…");
  const fileResourceId = await uploadTrackerFileResource(pdfFile);
  await waitForFileResourceStored(fileResourceId);

  onStatus?.("Attaching document to tracker timeline…");
  const programStageId = await getDocumentAttachmentProgramStageId();
  const existingEvent = await findAttachmentEvent(trackedEntityId, programStageId);
  const occurredAt = new Date().toISOString().slice(0, 10);
  const draftEventId = existingEvent?.event || generateDhis2Uid();

  const eventPayload = {
    events: [
      {
        ...(existingEvent?.event ? { event: existingEvent.event } : { event: draftEventId }),
        program: programId,
        programStage: programStageId,
        orgUnit: orgUnitId,
        trackedEntity: trackedEntityId,
        enrollment: enrollmentId,
        occurredAt,
        status: "COMPLETED",
        dataValues: [{ dataElement: attachmentDataElement, value: fileResourceId }],
      },
    ],
  };

  const { report: eventReport } = await submitAndPollTrackerImport(eventPayload, {
    importStrategy: existingEvent?.event ? "UPDATE" : "CREATE_AND_UPDATE",
    onStatus: () => onStatus?.("Saving document attachment event…"),
  });

  const eventId = extractImportedTrackerEventId(eventReport, draftEventId);

  return {
    trackedEntityId,
    enrollmentId,
    eventId,
    fileResourceId,
    documentReference,
    documentName: buildPetitionDocumentName(year),
  };
}
